export const DRAG_THRESHOLD_PIXELS = 5;
export const LAT_LON_DECIMALS = 7;
export const METERS_PER_DEGREE_LAT = 111132;
export const FEET_PER_METER = 3.280839895;
export const FEET_PER_DEGREE_LAT = METERS_PER_DEGREE_LAT * FEET_PER_METER;
export const TARGET_GROUND_COVERAGE_FEET = 200;
export const AERIAL_TRANSFORM_MIN_SCALE = 0.5;
export const AERIAL_TRANSFORM_MAX_SCALE = 8;
export const DEFAULT_FOCUS_TRANSFORM_SCALE = 0.5;
export const MARKER_FOCUS_ANIMATION_MS = 400;
export const GEOREF_BOUNDS_EPSILON = 1e-9;

export const clampNormalizedCoordinate = (coordinate) => Math.min(1, Math.max(0, coordinate));

export const roundLatLon = (value) => {
	const coordinate = Number(value);
	if (!Number.isFinite(coordinate)) {
		return 0;
	}
	return Number(coordinate.toFixed(LAT_LON_DECIMALS));
};

export const hasGeographicPosition = (marker) => {
	const lat = Number(marker?.lat);
	const lon = Number(marker?.lon);
	return Number.isFinite(lat) && Number.isFinite(lon) && (lat !== 0 || lon !== 0);
};

export const getImageGeoref = (image) => {
	const metadata = image?.metadata;
	const originLat = Number(metadata?.originLat);
	const originLon = Number(metadata?.originLon);
	const scale = Number(metadata?.scale);
	if (!Number.isFinite(originLat) || !Number.isFinite(originLon) || !Number.isFinite(scale) || scale <= 0) {
		return null;
	}
	return { originLat, originLon, scale };
};

export const getImageScaleFeet = (image) => {
	const scale = Number(image?.metadata?.scale);
	if (!Number.isFinite(scale) || scale <= 0) {
		return null;
	}
	return scale;
};

export const transformScaleForGroundCoverage = (
	imageScaleFeet,
	{
		minScale = AERIAL_TRANSFORM_MIN_SCALE,
		maxScale = AERIAL_TRANSFORM_MAX_SCALE,
		targetFeet = TARGET_GROUND_COVERAGE_FEET,
	} = {},
) => {
	const scale = Number(imageScaleFeet);
	if (!Number.isFinite(scale) || scale <= 0 || !Number.isFinite(targetFeet) || targetFeet <= 0) {
		return DEFAULT_FOCUS_TRANSFORM_SCALE;
	}
	return Math.min(maxScale, Math.max(minScale, (2 * scale) / targetFeet));
};

export const getMarkerFocusTransformScale = (image) => {
	const imageScaleFeet = getImageScaleFeet(image);
	if (imageScaleFeet == null) {
		return DEFAULT_FOCUS_TRANSFORM_SCALE;
	}
	return transformScaleForGroundCoverage(imageScaleFeet);
};

export const isGeographicPointInsideGeoref = (lat, lon, georef) => {
	const relativeCoordinates = latLonToRelativeCoordinates(lat, lon, georef);
	if (!relativeCoordinates) {
		return false;
	}
	return (
		relativeCoordinates.relativeXCord >= -GEOREF_BOUNDS_EPSILON &&
		relativeCoordinates.relativeXCord <= 1 + GEOREF_BOUNDS_EPSILON &&
		relativeCoordinates.relativeYCord >= -GEOREF_BOUNDS_EPSILON &&
		relativeCoordinates.relativeYCord <= 1 + GEOREF_BOUNDS_EPSILON
	);
};

export const getMarkerImageContainment = (marker, image) => {
	const georef = getImageGeoref(image);
	if (!marker || !hasGeographicPosition(marker) || !georef) {
		return "unknown";
	}
	return isGeographicPointInsideGeoref(marker.lat, marker.lon, georef) ? "inside" : "outside";
};

export const feetPerDegreeLongitude = (originLat) => FEET_PER_DEGREE_LAT * Math.cos((Number(originLat) * Math.PI) / 180);

export const latLonToRelativeCoordinates = (lat, lon, georef) => {
	if (!georef) {
		return null;
	}
	const eastFeet = (Number(lon) - georef.originLon) * feetPerDegreeLongitude(georef.originLat);
	const northFeet = (Number(lat) - georef.originLat) * FEET_PER_DEGREE_LAT;
	return {
		relativeXCord: 0.5 + eastFeet / (2 * georef.scale),
		relativeYCord: 0.5 - northFeet / (2 * georef.scale),
	};
};

export const relativeCoordinatesToLatLon = (relativeXCord, relativeYCord, georef) => {
	if (!georef) {
		return { lat: 0, lon: 0 };
	}
	const eastFeet = (Number(relativeXCord) - 0.5) * 2 * georef.scale;
	const northFeet = (0.5 - Number(relativeYCord)) * 2 * georef.scale;
	return {
		lat: roundLatLon(georef.originLat + northFeet / FEET_PER_DEGREE_LAT),
		lon: roundLatLon(georef.originLon + eastFeet / feetPerDegreeLongitude(georef.originLat)),
	};
};

export const projectMarkerToImage = (marker, image) => {
	const georef = getImageGeoref(image);
	if (!marker || !hasGeographicPosition(marker) || !georef) {
		return marker;
	}
	const relativeCoordinates = latLonToRelativeCoordinates(marker.lat, marker.lon, georef);
	if (!relativeCoordinates) {
		return marker;
	}
	return {
		...marker,
		relativeXCord: relativeCoordinates.relativeXCord,
		relativeYCord: relativeCoordinates.relativeYCord,
	};
};

export const projectMarkersToImage = (markers, image) => (markers || []).map((marker) => projectMarkerToImage(marker, image));

export const applyGeographicCoordinates = (marker, image) => {
	const georef = getImageGeoref(image);
	if (!marker) {
		return marker;
	}
	if (!georef) {
		return {
			...marker,
			lat: roundLatLon(marker.lat),
			lon: roundLatLon(marker.lon),
		};
	}
	return {
		...marker,
		...relativeCoordinatesToLatLon(marker.relativeXCord, marker.relativeYCord, georef),
	};
};

export const hasMovedPastDragThreshold = (
	startClientX,
	startClientY,
	currentClientX,
	currentClientY,
	thresholdPixels = DRAG_THRESHOLD_PIXELS,
) => {
	const horizontalDistance = currentClientX - startClientX;
	const verticalDistance = currentClientY - startClientY;
	return Math.hypot(horizontalDistance, verticalDistance) >= thresholdPixels;
};

export const pointerToNormalizedCoordinates = (clientX, clientY, imageElement) => {
	if (!imageElement) {
		return null;
	}

	const imageRectangle = imageElement.getBoundingClientRect();
	if (imageRectangle.width <= 0 || imageRectangle.height <= 0) {
		return null;
	}

	return {
		relativeXCord: clampNormalizedCoordinate((clientX - imageRectangle.left) / imageRectangle.width),
		relativeYCord: clampNormalizedCoordinate((clientY - imageRectangle.top) / imageRectangle.height),
	};
};

export const getMarkerScreenPoint = (marker, imageElement) => {
	if (!marker || !imageElement) {
		return null;
	}

	const imageRectangle = imageElement.getBoundingClientRect();
	if (imageRectangle.width <= 0 || imageRectangle.height <= 0) {
		return null;
	}

	return {
		clientX: imageRectangle.left + marker.relativeXCord * imageRectangle.width,
		clientY: imageRectangle.top + marker.relativeYCord * imageRectangle.height,
	};
};

export const isPointInsideRectangle = (clientX, clientY, rectangle) => {
	return clientX >= rectangle.left && clientX <= rectangle.right && clientY >= rectangle.top && clientY <= rectangle.bottom;
};

export const getCenteredTransformPosition = (markerClientX, markerClientY, viewportRectangle, positionX, positionY) => {
	const viewportCenterX = viewportRectangle.left + viewportRectangle.width / 2;
	const viewportCenterY = viewportRectangle.top + viewportRectangle.height / 2;
	return {
		positionX: positionX + (viewportCenterX - markerClientX),
		positionY: positionY + (viewportCenterY - markerClientY),
	};
};

export const getZoomCenteredTransformPosition = (
	markerClientX,
	markerClientY,
	wrapperRectangle,
	positionX,
	positionY,
	currentScale,
	targetScale,
) => {
	const scale = Number(currentScale);
	const nextScale = Number(targetScale);
	if (!Number.isFinite(scale) || scale === 0 || !Number.isFinite(nextScale)) {
		return { positionX, positionY };
	}

	const contentX = (markerClientX - wrapperRectangle.left - positionX) / scale;
	const contentY = (markerClientY - wrapperRectangle.top - positionY) / scale;
	return {
		positionX: wrapperRectangle.width / 2 - contentX * nextScale,
		positionY: wrapperRectangle.height / 2 - contentY * nextScale,
	};
};
